import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import api from "../../../api";
import { AuthContext } from "../../../context/AuthContext";
import "./CategoryMasterFields.css";

// One Category / Sub-category experience for every Data Analyst form that
// uses the categories master: My Performance -> Enter Monthly Data and
// My KRA Work (all record types). Same data and rules as Own Ticket:
//   - lists: GET /categories/technician-owned (the analyst's own categories)
//     and GET /subcategories/technician-owned/:categoryId (that category only)
//   - "+ Add New": POST /categories/own and /subcategories/own -- the server
//     trims, rejects duplicates (case-insensitive, under a lock) and keeps the
//     new row private to its creator; the server re-checks every saved id.
// The menu renders in a portal (fixed position) so it is never clipped by a
// scrolling modal body.

const ADD = "__add_new__";
const NAME_MAX = { category: 100, subcategory: 255 };
const COPY = {
  category: { add: "+ Add New Category", noun: "category", empty: "No categories yet." },
  subcategory: { add: "+ Add New Sub-category", noun: "sub-category", empty: "No sub-categories for this category yet." },
};

const portalTarget = typeof document !== "undefined" ? document.body : null;
const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderColor: state.isFocused ? "var(--primary, #6d1530)" : "#d6dbe3",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(109, 21, 48, 0.15)" : "none",
    "&:hover": { borderColor: state.isFocused ? "var(--primary, #6d1530)" : "#b8c0cc" },
  }),
  option: (base, state) => (state.data.isAdd
    ? { ...base, fontWeight: 700, color: "var(--primary, #6d1530)", borderTop: "1px solid #eef0f3", backgroundColor: state.isFocused ? "#fbeef1" : "#fff" }
    : { ...base, backgroundColor: state.isSelected ? "var(--primary, #6d1530)" : state.isFocused ? "#fbeef1" : base.backgroundColor }),
};

const cleanName = (raw) => String(raw || "").trim().replace(/\s+/g, " ");
const errorText = (err, fallback) => err?.response?.data?.message || err?.response?.data?.error || fallback;

// Loads the master lists for one form and creates new rows on demand.
export function useCategoryMaster({ enabled, categoryId, withSubcategories = true }) {
  const auth = useContext(AuthContext);
  const unitId = auth?.user?.unit_id || null;
  const [categories, setCategories] = useState({ rows: [], loading: false, error: "" });
  const [subcategories, setSubcategories] = useState({ rows: [], loading: false, error: "" });

  const loadCategories = useCallback(async () => {
    setCategories((s) => ({ ...s, loading: true, error: "" }));
    try {
      const res = await api.get("/categories/technician-owned");
      setCategories({ rows: Array.isArray(res.data) ? res.data : [], loading: false, error: "" });
    } catch (err) {
      setCategories({ rows: [], loading: false, error: errorText(err, "Could not load your categories.") });
    }
  }, []);

  const loadSubcategories = useCallback(async (catId) => {
    if (!catId) { setSubcategories({ rows: [], loading: false, error: "" }); return; }
    setSubcategories((s) => ({ ...s, loading: true, error: "" }));
    try {
      const res = await api.get(`/subcategories/technician-owned/${catId}`);
      setSubcategories({ rows: Array.isArray(res.data) ? res.data : [], loading: false, error: "" });
    } catch (err) {
      setSubcategories({ rows: [], loading: false, error: errorText(err, "Could not load the sub-categories.") });
    }
  }, []);

  useEffect(() => { if (enabled) loadCategories(); }, [enabled, loadCategories]);
  useEffect(() => { if (enabled && withSubcategories) loadSubcategories(categoryId); }, [enabled, withSubcategories, categoryId, loadSubcategories]);

  const createCategory = async (name) => {
    const res = await api.post("/categories/own", { name, unit_id: Number(unitId) });
    await loadCategories();
    return res.data?.id;
  };
  const createSubcategory = async (name) => {
    const res = await api.post("/subcategories/own", { name, category_id: Number(categoryId), description: null, tat_value: null, tat_unit: null });
    await loadSubcategories(categoryId);
    return res.data?.id;
  };

  return {
    categories, subcategories, unitId,
    retryCategories: loadCategories,
    retrySubcategories: () => loadSubcategories(categoryId),
    createCategory, createSubcategory,
  };
}

// Searchable single / multi select over master rows, with "+ Add New …"
// pinned at the bottom of the menu and an inline add panel.
export function MasterSelect({
  inputId, kind, isMulti = false, rows, savedNames = {}, value, loading, loadError, onRetry,
  disabled, placeholder, onChange, canAdd, cannotAddReason, onCreate,
}) {
  const copy = COPY[kind];
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState("");
  const addInputRef = useRef(null);
  const selectRef = useRef(null);

  const ids = isMulti ? (value || []).map(String) : value ? [String(value)] : [];
  const options = rows.map((r) => ({ value: String(r.id), label: r.name }));
  // A saved value that is not (or no longer) in the list still shows by name.
  for (const id of ids) if (!options.some((o) => o.value === id)) options.push({ value: id, label: savedNames[id] || `#${id}` });
  const selected = ids.map((id) => options.find((o) => o.value === id)).filter(Boolean);
  const menuOptions = canAdd ? [...options, { value: ADD, label: copy.add, isAdd: true }] : options;

  useEffect(() => { if (adding) addInputRef.current?.focus(); }, [adding]);

  const closeAdd = (focusSelect = true) => {
    setAdding(false);
    setName("");
    setAddError("");
    if (focusSelect) setTimeout(() => selectRef.current?.focus(), 0);
  };

  const select = (id) => {
    if (isMulti) onChange([...new Set([...ids, String(id)])]);
    else onChange(String(id));
  };

  const handleChange = (opt) => {
    const list = isMulti ? opt || [] : opt ? [opt] : [];
    if (list.some((o) => o.value === ADD)) { setAdding(true); return; }
    if (isMulti) onChange(list.map((o) => o.value));
    else onChange(opt ? opt.value : "");
  };

  const add = async () => {
    const clean = cleanName(name);
    if (!clean) { setAddError(`Enter a ${copy.noun} name.`); return; }
    if (clean.length > NAME_MAX[kind]) { setAddError(`The name must be ${NAME_MAX[kind]} characters or fewer.`); return; }
    const existing = rows.find((r) => cleanName(r.name).toLowerCase() === clean.toLowerCase());
    if (existing) { select(existing.id); closeAdd(); return; }
    setSaving(true);
    setAddError("");
    try {
      const newId = await onCreate(clean);
      if (newId) select(newId);
      closeAdd();
    } catch (err) {
      setAddError(errorText(err, `Could not add the ${copy.noun}. Please try again.`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Select
        ref={selectRef}
        inputId={inputId}
        classNamePrefix="kp-select"
        className="cm-select"
        isMulti={isMulti}
        isClearable={!isMulti}
        isSearchable
        isLoading={loading}
        isDisabled={disabled}
        placeholder={placeholder}
        options={menuOptions}
        value={isMulti ? selected : selected[0] || null}
        onChange={handleChange}
        // "+ Add New …" stays visible whatever is typed in the search box.
        filterOption={(opt, input) => opt.data.isAdd || !input || opt.label.toLowerCase().includes(input.toLowerCase())}
        noOptionsMessage={() => (loading ? "Loading…" : copy.empty)}
        loadingMessage={() => "Loading…"}
        menuPortalTarget={portalTarget}
        menuPosition="fixed"
        menuPlacement="auto"
        styles={selectStyles}
        aria-invalid={!!loadError}
      />
      {loadError && (
        <div className="cm-status cm-status-error" role="alert">
          {loadError} {onRetry && <button type="button" className="kp-link" onClick={onRetry}>Retry</button>}
        </div>
      )}
      {!loading && !loadError && !disabled && rows.length === 0 && !adding && (
        <div className="cm-status">{copy.empty}{canAdd ? ` Use "${copy.add}" to create one.` : ""}</div>
      )}
      {!canAdd && cannotAddReason && !disabled && <div className="cm-status">{cannotAddReason}</div>}
      {adding && (
        <div className="cm-add" role="group" aria-label={copy.add.replace("+ ", "")}>
          <div className="cm-add-row">
            <Form.Control
              ref={addInputRef}
              aria-label={`New ${copy.noun} name`}
              placeholder={`New ${copy.noun} name`}
              value={name}
              maxLength={NAME_MAX[kind]}
              disabled={saving}
              isInvalid={!!addError}
              onChange={(e) => { setName(e.target.value); if (addError) setAddError(""); }}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); add(); }
                if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); closeAdd(); }
              }}
            />
            <button type="button" className="tlp-btn tlp-btn-primary tlp-btn-sm" onClick={add} disabled={saving}>
              {saving ? "Saving…" : "Add"}
            </button>
            <button type="button" className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={() => closeAdd()} disabled={saving}>Cancel</button>
          </div>
          {addError && <div className="cm-status cm-status-error" role="alert">{addError}</div>}
        </div>
      )}
    </>
  );
}

// Category + dependent Sub-category, ready to drop into a form. The parent
// clears the sub-category when the category changes (onCategoryChange).
export function CategorySubcategoryFields({
  idPrefix, categoryLabel = "Category", subLabel = "Sub-category", categoryRequired = false, subRequired = false,
  categoryId, onCategoryChange, subValue, onSubChange, subMulti = false, showSub = true,
  savedCategoryName, savedSubNames = {}, disabled = false, groupClassName = "",
}) {
  const master = useCategoryMaster({ enabled: !disabled, categoryId: categoryId || null, withSubcategories: showSub });
  const catSaved = categoryId && savedCategoryName ? { [String(categoryId)]: savedCategoryName } : {};
  const waitingForCategory = !categoryId;
  return (
    <>
      <Form.Group className={groupClassName}>
        <Form.Label htmlFor={`${idPrefix}-category`}>{categoryLabel}{categoryRequired && <span className="text-danger"> *</span>}</Form.Label>
        <MasterSelect
          inputId={`${idPrefix}-category`} kind="category" rows={master.categories.rows} savedNames={catSaved}
          value={categoryId} loading={master.categories.loading} loadError={master.categories.error} onRetry={master.retryCategories}
          disabled={disabled} placeholder="Search or select a category…" onChange={onCategoryChange}
          canAdd={!!master.unitId} cannotAddReason={master.unitId ? "" : "Your account has no unit, so a new category cannot be added here."}
          onCreate={master.createCategory}
        />
      </Form.Group>
      {showSub && (
        <Form.Group className={groupClassName}>
          <Form.Label htmlFor={`${idPrefix}-subcategory`}>{subLabel}{subRequired && <span className="text-danger"> *</span>}</Form.Label>
          <MasterSelect
            key={`sub-${categoryId || "none"}`}
            inputId={`${idPrefix}-subcategory`} kind="subcategory" isMulti={subMulti} rows={master.subcategories.rows} savedNames={savedSubNames}
            value={subValue} loading={master.subcategories.loading} loadError={master.subcategories.error} onRetry={master.retrySubcategories}
            disabled={disabled || waitingForCategory}
            placeholder={waitingForCategory ? "Select a category first" : "Search or select a sub-category…"}
            onChange={onSubChange} canAdd={!waitingForCategory} onCreate={master.createSubcategory}
          />
          {waitingForCategory && !disabled && <div className="cm-status">Select a category first.</div>}
        </Form.Group>
      )}
    </>
  );
}

export default CategorySubcategoryFields;
